import React from 'react';
import ProposalImage from './ProposalImage';
import VariableImage from '../../../media/VariableImage';
import { puckImageField } from '../../../puck/fields/puckImageField';
import { puckDebouncedTextField } from '../../../puck/fields/puckDebouncedTextField';
import { puckPxWidthField } from '../../../puck/fields/puckPxWidthField';
import { puckHorizontalAlignField } from '../../../puck/fields/puckHorizontalAlignField';
import { puckVerticalAlignField } from '../../../puck/fields/puckVerticalAlignField';
import { defaultMediaPuckProps } from '../../../media';
import { puckSectionField } from '../../../puck/fields/puckSectionField';
import { puckColorField } from '../../../puck/fields/createPuckColorField';
import { puckOpacityField } from '../../../puck/fields/puckOpacityField';
import { DEFAULT_BORDER_COLOR } from '../../../../shared/boxes/borderStyles';
import { resolveVariableBoxFields, variableBoxFormFields } from '../../../puck/fields/variableBoxFormFields';
import { isSiteVariableBox, ownBox, WEB_IMAGE_BLOCK_TYPE } from '../../../utils/siteVariableBoxes';
import { puckSwitchField } from '../../../puck/fields/puckSwitchField';
import HiddenBlockFrame from '../../../puck/HiddenBlockFrame';

const proposalImageConfig = {
  label: 'Image',
  defaultProps: {
    src: '',
    /* Paliers du média de la banque, rangés à côté de `src` : `src` reste une
       chaîne (la forme déjà en production), ces props disent ce qu'elle ne dit
       pas. Voir `useMediaMetaProps`. */
    srcMediaId: null,
    srcResolution: 'auto',
    srcSrcset: '',
    alt: '',
    allowEnlarge: false,
    alignHorizontal: 'center',
    alignVertical: 'center',
    maxWidth: undefined,
    width: undefined,
    height: undefined,
    backgroundColor: '',
    borderColor: '',
    borderWidth: 0,
    opacity: undefined,
    ...defaultMediaPuckProps,
    // Box variable (img_variable du classique) : réglages de formulaire de
    // l'image (`imageSlots`). `showInForm` n'a pas de défaut : absent, c'est
    // le lien au CRM qui décide (shared/variables).
    srcForm: {},
  },
  // Bloc image UNIQUEMENT (la vidéo a son propre bloc VideoBlock).
  // Pas de champ « affichage » (cover/contain) : sans ratio imposé les deux
  // options rendent identique — champ mensonger retiré. mediaFit /
  // mediaAspectRatio restent des props rendues (legacy).
  fields: {
    _section1: puckSectionField('Média'),
    src: puckImageField('Image', { focalPointProp: 'mediaFocalPoint' }),
    allowEnlarge: puckSwitchField('Autoriser l’agrandissement'),
    alt: puckDebouncedTextField("Description de l'image"),
    // Le champ édite alignHorizontal (la prop réellement utilisée au rendu) —
    // l'ancien champ écrivait `alignment`, écrasé par alignHorizontal du parser.

    _section2: puckSectionField('Style'),
    /* Ces quatre props étaient déjà rendues (et importées du proposal) sans
       champ pour les régler. Le fond vide est transparent. */
    backgroundColor: puckColorField('Couleur de fond', { allowTransparent: true }),
    /* Vide, la bordure prend le gris par défaut des box : le champ l'affiche
       plutôt qu'un blanc que le rendu n'applique pas. */
    borderColor: puckColorField('Couleur des bordures', {
      glyph: 'stroke',
      emptyIsInherited: true,
      readInherited: () => DEFAULT_BORDER_COLOR,
    }),
    borderWidth: {
      type: 'number', label: 'Taille de la bordure', min: 0, max: 20, step: 1,
    },
    opacity: puckOpacityField('Opacité'),

    _section3: puckSectionField('Affichage'),
    alignHorizontal: puckHorizontalAlignField(),
    alignVertical: puckVerticalAlignField(undefined, { stretch: true }),
    // Un seul champ de taille : `width` est la valeur effective au rendu
    // (l'ancien champ éditait maxWidth, sans effet dès que width — importé
    // du proposal — était posé). maxWidth reste une prop pour le legacy.
    width: puckPxWidthField('Largeur'),

    ...variableBoxFormFields(),
  },
  resolveFields: (data, { fields }) => resolveVariableBoxFields(
    { type: WEB_IMAGE_BLOCK_TYPE, props: data.props },
    fields,
  ),
  render: ({
    id, src, srcSrcset, srcVariable, alt, alignment, alignHorizontal, alignVertical, drawMode,
    maxWidth, width, height,
    backgroundColor, borderColor, borderWidth, opacity, mediaFit, mediaAspectRatio, mediaPosition, mediaFocalPoint, puck,
    srcForm, isVisible, allowEnlarge,
  }) => (
    <HiddenBlockFrame isVisible={isVisible} isEditing={!!puck?.isEditing}>
      <VariableImage
        blockId={id}
        urlProp="src"
        src={src}
        binding={srcVariable}
        variableBox={isSiteVariableBox(ownBox({ type: WEB_IMAGE_BLOCK_TYPE, props: { srcForm, srcVariable } }))}
        isEditing={!!puck?.isEditing}
        renderImage={(imageSrc) => (
          <ProposalImage
            id={id}
            src={imageSrc}
            srcSet={imageSrc === src && !srcVariable?.variableKey ? srcSrcset : undefined}
            alt={alt}
            allowEnlarge={allowEnlarge}
            drawMode={drawMode}
            alignment={alignment}
            alignHorizontal={alignHorizontal}
            alignVertical={alignVertical}
            maxWidth={maxWidth}
            width={width}
            height={height}
            backgroundColor={backgroundColor}
            borderColor={borderColor}
            borderWidth={borderWidth}
            opacity={opacity}
            mediaFit={mediaFit}
            mediaAspectRatio={mediaAspectRatio}
            mediaPosition={mediaPosition}
            mediaFocalPoint={mediaFocalPoint}
            isEditing={!!puck?.isEditing}
          />
        )}
      />
    </HiddenBlockFrame>
  ),
};

export default proposalImageConfig;
